import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, Menu, X } from "lucide-react";
import { LogoMark, WhatsAppIcon } from "./Logo";
import { BRAND, waLink, telLink } from "../lib/brand";

const LINKS = [
  { label: "Listings", href: "#listings" },
  { label: "Services", href: "#services" },
  { label: "Team", href: "#team" },
  { label: "Reviews", href: "#reviews" },
  { label: "Contact", href: "#contact" },
];

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    const el = document.querySelector(href);
    if (window.__lenis) {
      window.__lenis.scrollTo(el || href, { offset: -80, duration: 1.2 });
    } else if (el) {
      el.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      className="fixed top-0 left-0 right-0 z-50"
      data-testid="site-header"
    >
      <div
        className={`transition-all duration-500 ${
          scrolled
            ? "bg-[#faf9f5]/85 backdrop-blur-xl border-b border-[#e7e3d7] shadow-[0_8px_30px_rgba(19,19,17,0.06)]"
            : "bg-transparent border-b border-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
          <div className="flex items-center justify-between h-[72px] lg:h-20">
            <button
              onClick={() => go("#top")}
              className="flex items-center gap-3 group"
              data-testid="header-logo"
            >
              <LogoMark className="h-9 w-9 lg:h-10 lg:w-10 transition-transform duration-500 group-hover:rotate-[8deg]" />
              <span className="flex flex-col leading-none text-left">
                <span className="font-serif text-lg lg:text-xl font-semibold tracking-tight text-[#131311]">
                  Laxmi Jiwan
                </span>
                <span className="text-[9px] lg:text-[10px] tracking-[0.42em] uppercase text-[#9a7b38] font-medium mt-0.5">
                  Properties · Hisar
                </span>
              </span>
            </button>

            <nav className="hidden lg:flex items-center gap-9">
              {LINKS.map((l) => (
                <button
                  key={l.href}
                  onClick={() => go(l.href)}
                  className="lj-underline-link text-sm font-medium text-[#52514c] hover:text-[#131311] transition-colors"
                  data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                >
                  {l.label}
                </button>
              ))}
            </nav>

            <div className="hidden lg:flex items-center gap-3">
              <a
                href={telLink}
                className="flex items-center gap-2 text-sm font-medium text-[#131311] hover:text-[#9a7b38] transition-colors"
                data-testid="header-phone"
              >
                <Phone className="h-4 w-4" strokeWidth={2} />
                {BRAND.phoneDisplay}
              </a>
              <a
                href={waLink("Hello Laxmi Jiwan Properties, I'd like to know more about your listings in Hisar.")}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 rounded-full bg-[#131311] text-[#faf9f5] px-5 py-2.5 text-sm font-semibold hover:bg-[#9a7b38] transition-colors duration-300"
                data-testid="header-whatsapp-button"
              >
                <WhatsAppIcon className="h-4 w-4" />
                Chat
              </a>
            </div>

            <button
              className="lg:hidden p-2 text-[#131311]"
              onClick={() => setOpen((v) => !v)}
              data-testid="mobile-menu-toggle"
              aria-label="Menu"
            >
              {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden overflow-hidden bg-[#faf9f5]/95 backdrop-blur-xl border-b border-[#e7e3d7]"
          >
            <div className="px-6 py-6 flex flex-col gap-1">
              {LINKS.map((l) => (
                <button
                  key={l.href}
                  onClick={() => go(l.href)}
                  className="text-left py-3 font-serif text-2xl text-[#131311] border-b border-[#e7e3d7]/70"
                >
                  {l.label}
                </button>
              ))}
              <div className="flex gap-3 mt-5">
                <a
                  href={telLink}
                  className="flex-1 flex items-center justify-center gap-2 rounded-full border border-[#131311] py-3 text-sm font-semibold"
                >
                  <Phone className="h-4 w-4" /> Call
                </a>
                <a
                  href={waLink("Hello Laxmi Jiwan Properties, I'd like to know more about your listings in Hisar.")}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 flex items-center justify-center gap-2 rounded-full bg-[#25d366] text-white py-3 text-sm font-semibold"
                >
                  <WhatsAppIcon className="h-4 w-4" /> WhatsApp
                </a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
